import CollectionItem from "@/components/molecules/collection-item";
import { __, getPermalink } from "@/lib/i18n/functions";
import { cn, extractKeyTextValue, getMedusaProductPrice } from "@/lib/utils";
import { ProductDocument } from "@/prismicio-types";
import { Loader2, ShoppingCart } from "lucide-react";
import { useRef } from "react";
import { StoreProduct } from "@medusajs/types";
import { Locales } from "@/lib/i18n";
import { Button, buttonVariants } from "@/components/atoms/buttons";
import { useAddToCart } from "@/hooks/useCartMutations";
import { Toast, toast } from "@/components/atoms/toast";
import { useCart } from "@/hooks/useCart";
import Link from "next/link";
import { resolveProductConditioning } from "@/lib/product-units";

type CollectionProduct = {
  collectionProducts: ProductDocument[] | null;
  medusaProducts: Record<string, StoreProduct> | undefined;
  locale: Locales;
  isLoading: boolean;
};

const ProductCollection = ({ collectionProducts, medusaProducts, isLoading, locale }: CollectionProduct) => {
  const { cart } = useCart();
  const quantitiesRef = useRef<Record<string, number>>({});

  const products = Object.values(medusaProducts || {});

  const { addToCart, isLoading: isAddingAllToCart } = useAddToCart({
    onError: (error) => {
      console.error("Failed to add item to cart:", error);
      toast.error({ title: __("Failed to add item to cart", locale) });
    },
  });

  const updateProductQuantity = (medusaId: string, quantity: number) => {
    quantitiesRef.current[medusaId] = quantity;
  };

  const handleAddToCart = async () => {
    if (!medusaProducts) return;

    try {
      const addToCartPromises = products
        .filter((product) => product.status !== "draft")
        .map(async (product) => {
          if (!product?.variants?.[0]?.id) {
            console.warn(`No variant found for product ${product.id}`);
            return;
          }

          const isInCart = cart?.items?.some((item) => item.variant_id === product.variants?.[0]?.id);
          if (isInCart) return;

          const quantity = quantitiesRef.current[product.id] || 0;
          if (quantity > 0) {
            return addToCart(product.variants[0].id, quantity);
          }
        });

      await Promise.all(addToCartPromises);

      toast.success({
        title: __("All products added to cart!", locale),
      });
    } catch (error) {
      console.error("Error adding products to cart:", error);
      toast.error({
        title: __("Failed to add some products to cart", locale),
      });
    }
  };

  const isProductsInCart = products
    .filter((product) => product.status !== "draft")
    .every((product) => {
      return cart?.items && cart.items.some((item) => item.product_handle === product?.handle);
    });

  const isAllProductsOutOfStock = products.every((product) => product.status === "draft");

  return (
    <>
      <div className="flex flex-col gap-3.5 border-t pt-3.5">
        <div className="flex flex-col gap-3">
          <dl className="text-primary-light flex items-center text-sm">
            <dt
              className={cn("flex-[7] sm:flex-[4]", isProductsInCart ? "min-[480px]:flex-[9] sm:flex-[4]" : "flex-[4]")}
            >
              {__("Product", locale)}
            </dt>
            <dt className="hidden flex-[2] md:block lg:hidden xl:block">{__("Ref.", locale)}</dt>
            <dt className="flex-[3] sm:flex-[2]">{__("Price", locale)}</dt>
            <dt className={cn("flex-[3] text-center", isProductsInCart && !isAllProductsOutOfStock && "text-right")}>
              {__("Quantity", locale)}
            </dt>
          </dl>
          {[...(collectionProducts || [])]?.map((collection, index) => {
            const medusaId = extractKeyTextValue(collection.data.medusaId);
            const medusaItem = medusaProducts?.[medusaId];

            return (
              <CollectionItem
                key={index}
                locale={locale}
                onQuantityChange={(quantity) => updateProductQuantity(medusaItem?.id || "", quantity)}
                isLoading={isLoading}
                isProductsInCart={isProductsInCart}
                productInfo={{
                  medusaId: medusaId || "",
                  variantId: medusaItem?.variants?.[0].id || "",
                  productName: collection.data.title!,
                  productPrice: getMedusaProductPrice(medusaItem),
                  productQuantity: 10,
                  reference: medusaItem?.variants?.[0].sku || "ACBROMO",
                  productConditioning: resolveProductConditioning(
                    medusaItem?.metadata?.conditioning,
                    medusaItem?.metadata?.conditioning_bac
                  ),
                  medusaPrice: medusaItem?.variants?.[0].calculated_price?.calculated_amount || 0,
                  isOutOfStock: medusaItem?.status === "draft" ? true : false,
                }}
              />
            );
          })}
        </div>
      </div>
      {!isLoading && (
        <div className="border-primary-light/30 mt-auto border-t">
          {isAllProductsOutOfStock ? (
            <Toast
              id="collection-out-of-stock"
              type="info"
              className="mt-3 max-w-none shadow-none"
              title={__("This collection is out of stock", locale)}
            />
          ) : (
            <div className="mt-3 flex justify-end">
              {cart !== undefined &&
                (!isProductsInCart ? (
                  <Button
                    onClick={handleAddToCart}
                    disabled={isAddingAllToCart}
                    className="h-11 w-full gap-3 text-base transition-transform duration-150 ease-in-out active:scale-95 md:h-14 md:w-auto md:text-lg"
                  >
                    {isAddingAllToCart ? <Loader2 className="animate-spin" /> : <ShoppingCart size={20} />}
                    {__("Add to cart", locale)}
                  </Button>
                ) : (
                  <Link
                    href={getPermalink(locale, "cart")}
                    className={cn(
                      buttonVariants(),
                      "h-11 w-full gap-3 text-base transition-transform duration-150 ease-in-out active:scale-95 md:h-14 md:w-auto md:text-lg"
                    )}
                  >
                    <ShoppingCart size={20} />
                    {__("View Cart", locale)}
                  </Link>
                ))}
            </div>
          )}
        </div>
      )}
    </>
  );
};

export default ProductCollection;
